import type { Vector3 } from "three";
import {
  DRAG_COLORS,
  type DragLineStyle,
  type GuideLineStyle,
} from "./dragTheme";
import { StableLine } from "./StableLine";

const HELPER_USER_DATA = { userData: { isDragHelper: true } };

/** A point accepted by {@link StableLine}. */
export type CasedLinePoint = Vector3 | [number, number, number];

export interface CasedLineProps {
  /** Vertex list. With `segments`, consecutive pairs; otherwise a polyline. */
  points: CasedLinePoint[];
  /** Treat `points` as disjoint pairs rather than as one connected run. */
  segments?: boolean;
  /** Core colour, width and opacity, plus the casing that protects them. */
  style: DragLineStyle | GuideLineStyle;
  /** Render order of the casing; the core is drawn one above it. */
  renderOrder: number;
  /** Dash length in scene units. Ignored unless the style is dashed. */
  dashSize?: number;
  /** Dash gap in scene units. Ignored unless the style is dashed. */
  gapSize?: number;
}

/**
 * One drag-affordance stroke, drawn twice: a near-black casing with a coloured
 * core over it.
 *
 * This is the *only* way a guide stroke should be drawn. A single-colour line
 * cannot be made legible here, because the surface behind it is a panel colour
 * the user picked from an open, admin-editable list that spans white to black
 * by way of orange and yellow — see the note in `dragTheme.ts`. The pair works
 * the way a road label works on a map: whichever of the two layers the
 * background swallows, the other one survives.
 *
 * Both layers are drawn with {@link StableLine} (a `Line2`), because
 * `LineBasicMaterial.linewidth` is silently ignored in WebGL and a casing that
 * cannot be made wider than its core is not a casing at all. Not drei's
 * `<Line>`: it disposes the material along with the geometry whenever the
 * points change, and a guide stroke moves with the pointer — see `StableLine`.
 *
 * A dashed stroke dashes *both* layers on the same pattern. Dashing only the
 * core would leave an unbroken dark line running under the gaps, which reads as
 * a solid line in the wrong colour rather than as a dashed one.
 */
export const CasedLine = ({
  points,
  segments,
  style,
  renderOrder,
  dashSize,
  gapSize,
}: Readonly<CasedLineProps>) => {
  const dashed = "dashed" in style ? style.dashed : false;
  const shared = {
    points,
    segments,
    dashed,
    dashSize,
    gapSize,
    ...HELPER_USER_DATA,
  } as const;

  return (
    <>
      <StableLine
        {...shared}
        color={DRAG_COLORS.guideCasing}
        opacity={style.casingOpacity}
        lineWidth={style.casingWidth}
        renderOrder={renderOrder}
      />
      <StableLine
        {...shared}
        color={style.color}
        opacity={style.opacity}
        lineWidth={style.lineWidth}
        renderOrder={renderOrder + 1}
      />
    </>
  );
};

export default CasedLine;
